import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  Select,
  useColorModeValue,
  Spinner,
  Box,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../../utils/init-firebase';
import { COLLECTION_NAMES } from '../../types';

interface GenerateSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (meetingTimestamp: Timestamp) => Promise<void>;
}

/**
 * Generate Summary Modal Component
 * Allows selection of a past meeting to generate a summary for
 */
export function GenerateSummaryModal({
  isOpen,
  onClose,
  onGenerate,
}: GenerateSummaryModalProps) {
  const [availableMeetings, setAvailableMeetings] = useState<
    Array<{ timestamp: Timestamp; date: string }>
  >([]);
  const [selectedMeeting, setSelectedMeeting] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const textColor = useColorModeValue('gray.700', 'gray.300');

  useEffect(() => {
    if (isOpen) {
      loadAvailableMeetings();
    }
  }, [isOpen]);

  const loadAvailableMeetings = async () => {
    setLoading(true);
    try {
      // Get unique meeting timestamps from l10_notes
      const [notesSnapshot, summariesSnapshot] = await Promise.all([
        getDocs(
          query(
            collection(db, COLLECTION_NAMES.L10_NOTES),
            orderBy('meetingTimestamp', 'desc')
          )
        ),
        getDocs(
          query(
            collection(db, COLLECTION_NAMES.MEETING_SUMMARIES),
            orderBy('meetingTimestamp', 'desc')
          )
        ),
      ]);

      // Get all meeting timestamps that already have summaries
      const summariesTimestamps = new Set<string>();
      summariesSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const timestamp = data.meetingTimestamp;
        if (timestamp) {
          const timestampStr = timestamp instanceof Timestamp
            ? timestamp.toMillis().toString()
            : timestamp.toString();
          summariesTimestamps.add(timestampStr);
        }
      });

      // Get unique meeting timestamps from l10_notes that don't have summaries
      const meetingsMap = new Map<string, { timestamp: Timestamp; date: string }>();
      notesSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const timestamp = data.meetingTimestamp;
        if (timestamp) {
          const timestampKey = timestamp instanceof Timestamp
            ? timestamp.toMillis().toString()
            : timestamp.toString();
          
          // Only include meetings without summaries
          if (!summariesTimestamps.has(timestampKey) && !meetingsMap.has(timestampKey)) {
            const timestampObj = timestamp instanceof Timestamp ? timestamp : Timestamp.fromDate(new Date(timestamp));
            meetingsMap.set(timestampKey, {
              timestamp: timestampObj,
              date: data.meetingDate || (timestampObj instanceof Timestamp ? timestampObj.toDate().toISOString().split('T')[0] : timestampKey),
            });
          }
        }
      });

      setAvailableMeetings(Array.from(meetingsMap.values()));
    } catch (error) {
      console.error('Error loading meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedMeeting) return;

    setGenerating(true);
    try {
      const meeting = availableMeetings.find(
        (m) => m.timestamp.toDate().toISOString() === selectedMeeting
      );
      if (meeting) {
        await onGenerate(meeting.timestamp);
        onClose();
        setSelectedMeeting('');
      }
    } catch (error) {
      console.error('Error generating summary:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Generate Meeting Summary</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <Box textAlign="center" py={4}>
              <Spinner size="lg" />
            </Box>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Text color={textColor}>
                Select a past meeting to generate a summary for:
              </Text>
              {availableMeetings.length === 0 ? (
                <Text color={textColor} fontSize="sm" fontStyle="italic">
                  All meetings have summaries. No meetings available for summary generation.
                </Text>
              ) : (
                <Select
                  value={selectedMeeting}
                  onChange={(e) => setSelectedMeeting(e.target.value)}
                  placeholder="Select a meeting"
                >
                  {availableMeetings.map((meeting, index) => {
                    const dateStr = meeting.timestamp instanceof Timestamp
                      ? meeting.timestamp.toDate().toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : meeting.date;
                    const timeStr = meeting.timestamp instanceof Timestamp
                      ? meeting.timestamp.toDate().toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                      : '';
                    return (
                      <option
                        key={index}
                        value={meeting.timestamp.toDate().toISOString()}
                      >
                        {dateStr} {timeStr && `at ${timeStr}`}
                      </option>
                    );
                  })}
                </Select>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleGenerate}
            isLoading={generating}
            isDisabled={!selectedMeeting || loading}
          >
            Generate Summary
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

